import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { supabase, LETTERS_BUCKET } from "../config";
import LetterAudio, { audioType } from "../components/LetterAudio";

const MAX_MB = 15;
const TYPES = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  heic: "image/heic",
  heif: "image/heif",
};

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  whatsapp: "",
  dedicated_to: "",
  place: "",
  letter_text: "",
  consent_public: false,
  consent_own_words: false,
};

// Some browsers give HEIC files no type, so fall back to the extension.
function photoType(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const type = (file.type || TYPES[ext] || "").toLowerCase();
  const extFor = { "image/jpeg": "jpg", "image/png": "png", "image/heic": "heic", "image/heif": "heif" };
  return extFor[type] ? { type, ext: extFor[type] } : null;
}

function Letters() {
  const location = useLocation();
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const [audio, setAudio] = useState(null); // { blob, name, source }
  const [audioError, setAudioError] = useState("");
  const [recordingNow, setRecordingNow] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const formTop = useRef(null);

  // Coming from the thank-you after a contribution: start with their name and dedication.
  useEffect(() => {
    const s = location.state || {};
    if (s.first_name || s.dedicated_to) {
      setForm((f) => ({
        ...f,
        first_name: s.first_name || f.first_name,
        last_name: s.last_name || f.last_name,
        dedicated_to: s.dedicated_to || f.dedicated_to,
      }));
    }
  }, [location.state]);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: "" }));
  };

  const pickPhoto = (e) => {
    const file = e.target.files && e.target.files[0];
    setErrors((er) => ({ ...er, photo: "" }));
    setPreview("");
    setPhoto(null);
    if (!file) return;
    if (!photoType(file)) {
      setErrors((er) => ({ ...er, photo: "Please choose a JPG, PNG or HEIC photo." }));
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setErrors((er) => ({ ...er, photo: `That photo is larger than ${MAX_MB} MB. Please send a smaller one.` }));
      return;
    }
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Please enter your first name";
    const email = form.email.trim();
    if (!email && !form.whatsapp) errs.contact = "Please give an email or a WhatsApp number";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Please check the email address";
    if (!photo) errs.photo = errs.photo || "Please choose the photo of your letter";
    if (!form.consent_public || !form.consent_own_words) errs.consent = "Please tick both boxes";
    if (recordingNow) errs.recording = "Please stop the recording first";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      if (errs.recording) setAudioError(errs.recording);
      return;
    }

    setSubmitting(true);
    try {
      const { type, ext } = photoType(photo);
      const path = `incoming/${crypto.randomUUID()}.${ext}`;
      const up = await supabase.storage
        .from(LETTERS_BUCKET)
        .upload(path, photo, { contentType: type, upsert: false });
      if (up.error) throw up.error;

      let audioPath = null;
      if (audio) {
        const a = audioType(audio.blob, audio.name);
        audioPath = `incoming/${crypto.randomUUID()}.${a.ext}`;
        const upA = await supabase.storage
          .from(LETTERS_BUCKET)
          .upload(audioPath, audio.blob, { contentType: a.type, upsert: false });
        if (upA.error) throw upA.error;
      }

      const { error } = await supabase.rpc("sritaru_letter_submit", {
        p_first_name: form.first_name.trim(),
        p_last_name: form.last_name.trim(),
        p_email: form.email.trim(),
        p_whatsapp: form.whatsapp,
        p_dedicated_to: form.dedicated_to.trim(),
        p_image_path: path,
        p_consent_public: form.consent_public,
        p_consent_own_words: form.consent_own_words,
        p_audio_path: audioPath,
        p_audio_source: audio ? audio.source : null,
        p_place: form.place.trim(),
        p_letter_text: form.letter_text.trim(),
        p_client_tz: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      });
      if (error) throw error;
      setSubmitted(true);
      formTop.current?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-sritaru-400 focus:border-sritaru-400 outline-none transition-colors";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const errorClass = "text-red-500 text-xs mt-1";

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-sritaru-700 via-sritaru-600 to-sritaru-800 text-white py-14 sm:py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-5xl mb-3">&#9997;&#65039;</div>
          <h1 className="text-3xl sm:text-5xl font-bold mb-4">A Letter to a Tree</h1>
          <p className="font-krushna text-xl sm:text-2xl text-sritaru-100 mb-4">
            మీ చేతిరాతతో మీ చెట్టుకు ఒక చిన్న ఉత్తరం రాయండి
          </p>
          <p className="text-base sm:text-lg text-sritaru-100">
            Your tree will grow on a farmer's land for years. Tell it, in your own
            handwriting, why you planted it, or write to the person you dedicated
            it to. We will share some of these letters, with your permission, in
            SriTaru Sankalpam reels and posts.
          </p>
        </div>
      </section>

      {/* How */}
      <section className="py-12 px-4 bg-sritaru-50">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: "\u{1F4DD}",
              title: "1. Write",
              desc: "A short letter, a few lines is enough, to your tree or to the person it is dedicated to. Plain paper, dark pen, any language.",
            },
            {
              icon: "\u{1F4F7}",
              title: "2. Photograph",
              desc: "Lay the page flat in daylight, near a window. Hold the phone straight above it, the whole page in the frame, no shadows.",
            },
            {
              icon: "\u{1F4E4}",
              title: "3. Upload",
              desc: "Send the photo with the form below. If you like, read it aloud too: record it right here or upload an audio file. We look at every letter before anything is shared.",
            },
          ].map((s) => (
            <div key={s.title} className="bg-white rounded-xl shadow-md p-6 text-center">
              <div className="text-4xl mb-3">{s.icon}</div>
              <h3 className="text-lg font-bold text-sritaru-700 mb-2">{s.title}</h3>
              <p className="text-gray-600 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section ref={formTop} className="py-12 px-4 bg-white scroll-mt-16">
        {submitted ? (
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg mx-auto text-center border border-sritaru-100">
            <div className="text-5xl mb-4">&#127795;</div>
            <h2 className="text-2xl font-bold text-sritaru-700 mb-2">Thank you, {form.first_name.trim()}!</h2>
            <p className="font-krushna text-lg text-sritaru-800 mb-3">మీ ఉత్తరం మాకు చేరింది. ధన్యవాదాలు!</p>
            <p className="text-gray-700 mb-6">
              Your letter has reached us. We read every letter before sharing it,
              and only your first name is ever shown with it.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/wall"
                className="bg-sritaru-600 text-white px-6 py-2 rounded-lg hover:bg-sritaru-700 transition-colors"
              >
                View Contributor Wall
              </Link>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setForm({ ...EMPTY, first_name: form.first_name, last_name: form.last_name, email: form.email, whatsapp: form.whatsapp });
                  setPhoto(null);
                  setPreview("");
                  setAudio(null);
                }}
                className="bg-earth-500 text-white px-6 py-2 rounded-lg hover:bg-earth-600 transition-colors"
              >
                Send another letter
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 sm:p-8 max-w-lg mx-auto space-y-5 border border-sritaru-100">
            <h2 className="text-2xl font-bold text-sritaru-800 text-center">Send your letter</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="letter-first_name" className={labelClass}>First Name *</label>
                <input id="letter-first_name" type="text" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputClass} />
                {errors.first_name && <p className={errorClass}>{errors.first_name}</p>}
              </div>
              <div>
                <label htmlFor="letter-last_name" className={labelClass}>Last Name</label>
                <input id="letter-last_name" type="text" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={inputClass} />
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                Email or WhatsApp, so we can reach you about your letter. These are private and never shown.
              </p>
              <div>
                <label htmlFor="letter-email" className={labelClass}>Email</label>
                <input
                  id="letter-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => {
                    set("email", e.target.value);
                    if (errors.contact) setErrors((er) => ({ ...er, contact: "" }));
                  }}
                  className={inputClass}
                />
                {errors.email && <p className={errorClass}>{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="letter-whatsapp" className={labelClass}>WhatsApp Number</label>
                <PhoneInput
                  id="letter-whatsapp"
                  international
                  defaultCountry="US"
                  value={form.whatsapp}
                  onChange={(val) => {
                    set("whatsapp", val || "");
                    if (errors.contact) setErrors((er) => ({ ...er, contact: "" }));
                  }}
                  className={inputClass}
                />
              </div>
              {errors.contact && <p className={errorClass}>{errors.contact}</p>}
            </div>

            <div>
              <label htmlFor="letter-dedicated_to" className={labelClass}>Tree dedicated to (optional)</label>
              <input
                id="letter-dedicated_to"
                type="text"
                value={form.dedicated_to}
                onChange={(e) => set("dedicated_to", e.target.value)}
                placeholder="In memory of / In honor of..."
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="letter-place" className={labelClass}>Where are you writing from? (optional)</label>
              <input
                id="letter-place"
                type="text"
                value={form.place}
                onChange={(e) => set("place", e.target.value)}
                placeholder="City, e.g. Dallas or Hyderabad"
                maxLength={120}
                className={inputClass}
              />
              <p className="text-xs text-gray-500 mt-1">Shown in small letters in the corner of your letter.</p>
            </div>

            <div>
              <label htmlFor="letter-photo" className={labelClass}>Photo of your letter *</label>
              <input
                id="letter-photo"
                type="file"
                accept="image/*,.heic,.heif"
                onChange={pickPhoto}
                className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sritaru-100 file:text-sritaru-800 hover:file:bg-sritaru-200"
              />
              <p className="text-xs text-gray-500 mt-1">JPG, PNG or HEIC, up to {MAX_MB} MB.</p>
              {errors.photo && <p className={errorClass}>{errors.photo}</p>}
              {photo && (
                <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2 text-center">
                  {/* HEIC shows only in Safari; elsewhere the file name is enough. */}
                  <img
                    src={preview}
                    alt="Your letter"
                    className="max-h-80 mx-auto rounded"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {photo.name} · {(photo.size / 1024 / 1024).toFixed(1)} MB
                  </p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="letter-text" className={labelClass}>Type your letter (optional)</label>
              <textarea
                id="letter-text"
                value={form.letter_text}
                onChange={(e) => set("letter_text", e.target.value)}
                rows={5}
                maxLength={4000}
                placeholder="The same words as your handwritten letter, in any language."
                className={inputClass}
              />
              <p className="text-xs text-gray-500 mt-1">
                Helps us show your words on screen as your letter is read.
              </p>
            </div>

            <LetterAudio
              value={audio}
              onChange={setAudio}
              onRecording={setRecordingNow}
              error={audioError}
              setError={setAudioError}
            />

            <div className="space-y-3 bg-sritaru-50 rounded-lg p-4">
              <label className="flex items-start gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.consent_public}
                  onChange={(e) => {
                    set("consent_public", e.target.checked);
                    if (errors.consent) setErrors((er) => ({ ...er, consent: "" }));
                  }}
                  className="mt-1 h-4 w-4 accent-sritaru-600"
                />
                <span>
                  SriTaru Sankalpam / BPlusWithBhaskar may show my letter, my recording of
                  it (if I send one), my first name and the city I write from publicly on
                  social media and the website. *
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.consent_own_words}
                  onChange={(e) => {
                    set("consent_own_words", e.target.checked);
                    if (errors.consent) setErrors((er) => ({ ...er, consent: "" }));
                  }}
                  className="mt-1 h-4 w-4 accent-sritaru-600"
                />
                <span>The letter is my own words and handwriting, and the voice is mine. *</span>
              </label>
              {errors.consent && <p className={errorClass}>{errors.consent}</p>}
            </div>

            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{submitError}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-sritaru-600 text-white font-semibold py-3 rounded-lg hover:bg-sritaru-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending..." : "Send my letter"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}

export default Letters;
